import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tree,
  message,
  Space,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Tag,
  Skeleton
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FolderOpenOutlined,
  BookOutlined,
  DownloadOutlined,
  UserOutlined,
  SearchOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import * as XLSX from 'xlsx';

interface MoodleCategory {
  id: number;
  name: string;
  idnumber: string;
  description: string;
  parent: number;
  path: string;
  theme: string;
  visible: boolean;
  coursecount: number;
}

interface MoodleCourse {
  id: number;
  fullname: string;
  shortname: string;
  idnumber: string;
  categoryid: number;
  visible: boolean;
  startdate: number;
  enddate: number;
}

interface CategoryFormData {
  name: string;
  idnumber: string;
  description: string;
  parent: number;
}

interface CourseFormData {
  fullname: string;
  shortname: string;
  idnumber: string;
  categoryid: number;
}

interface EnrolledUser {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  department?: string;
  roles: Array<{
    roleid: number;
    name: string;
    shortname: string;
    sortorder: number;
  }>;
}

interface CategoryTreeNode extends DataNode {
  id: number;
  name: string;
  parent: number;
  children: CategoryTreeNode[];
}

const MoodleCategoryManagementPage: React.FC = () => {
  const [categories, setCategories] = useState<MoodleCategory[]>([]);
  const [categoriesTree, setCategoriesTree] = useState<CategoryTreeNode[]>([]);
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MoodleCategory | null>(null);
  const [editingCourse, setEditingCourse] = useState<MoodleCourse | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [courseForm] = Form.useForm();
  // States for enrolled users modal
  const [enrolledUsersModalVisible, setEnrolledUsersModalVisible] = useState(false);
  const [enrolledUsers, setEnrolledUsers] = useState<EnrolledUser[]>([]);
  const [filteredEnrolledUsers, setFilteredEnrolledUsers] = useState<EnrolledUser[]>([]);
  const [selectedCourseForUsers, setSelectedCourseForUsers] = useState<MoodleCourse | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearchText, setUsersSearchText] = useState('');

  // Load dữ liệu khi component mount
  useEffect(() => {
    fetchCategories();
    fetchCourses();
  }, []);

  const buildTree = (cats: MoodleCategory[]): CategoryTreeNode[] => {
    const map: { [key: number]: CategoryTreeNode } = {};

    cats.forEach(cat => {
      map[cat.id] = {
        key: cat.id.toString(),
        title: cat.name,
        id: cat.id,
        name: cat.name,
        parent: cat.parent,
        children: []
      };
    });

    const tree: CategoryTreeNode[] = [];
    cats.forEach(cat => {
      if (cat.parent && map[cat.parent]) {
        map[cat.parent].children.push(map[cat.id]);
      } else {
        tree.push(map[cat.id]);
      }
    });

    return tree;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/moodle/categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const cats = Array.isArray(data) ? data : (data && Array.isArray(data.categories) ? data.categories : []);
        setCategories(cats);
        setCategoriesTree(buildTree(cats));
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Không thể tải danh sách danh mục');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/v1/moodle/courses', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data || []);
      } else {
        throw new Error('Failed to fetch courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    }
  };

  // Fetch enrolled users for a course
  const fetchEnrolledUsers = async (courseId: number) => {
    try {
      setUsersLoading(true);
      setEnrolledUsers([]);
      setFilteredEnrolledUsers([]);
      const response = await fetch(`/api/v1/moodle/courses/${courseId}/enrolled-users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || [];
        const usersArray = Array.isArray(data) ? data : [];
        setEnrolledUsers(usersArray);
        setFilteredEnrolledUsers(usersArray);
      } else {
        throw new Error('Failed to fetch enrolled users');
      }
    } catch (error) {
      console.error('Error fetching enrolled users:', error);
      message.error('Không thể tải danh sách học viên');
    } finally {
      setUsersLoading(false);
    }
  };

  // Show enrolled users modal
  const showEnrolledUsersModal = (course: MoodleCourse) => {
    setSelectedCourseForUsers(course);
    setEnrolledUsersModalVisible(true);
    setUsersSearchText('');
    fetchEnrolledUsers(course.id);
  };

  // Close enrolled users modal
  const handleEnrolledUsersCancel = () => {
    setEnrolledUsersModalVisible(false);
    setSelectedCourseForUsers(null);
    setEnrolledUsers([]);
    setFilteredEnrolledUsers([]);
    setUsersSearchText('');
  };

  // Search/filter enrolled users
  const handleUsersSearch = (value: string) => {
    setUsersSearchText(value);
    if (!value.trim()) {
      setFilteredEnrolledUsers(enrolledUsers);
      return;
    }
    const searchLower = value.toLowerCase();
    const filtered = enrolledUsers.filter(user =>
      user.username.toLowerCase().includes(searchLower) ||
      user.firstname.toLowerCase().includes(searchLower) ||
      user.lastname.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      (user.department && user.department.toLowerCase().includes(searchLower))
    );
    setFilteredEnrolledUsers(filtered);
  };

  const handleEditCategory = (cat: MoodleCategory) => {
    setEditingCategory(cat);
    setModalVisible(true);
    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        name: cat.name || '',
        idnumber: cat.idnumber || '',
        description: cat.description || '',
        parent: cat.parent !== undefined && cat.parent !== null ? cat.parent : undefined
      });
    }, 0);
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/moodle/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          message.success('Đã xóa danh mục');
          fetchCategories();
        } else {
          message.error(result.error || 'Không xóa được danh mục');
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Không xóa được danh mục');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('Lỗi xóa danh mục');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const url = editingCategory ? `/api/v1/moodle/categories` : '/api/v1/moodle/categories';
      const method = editingCategory ? 'PUT' : 'POST';
      const payload = editingCategory ? { ...editingCategory, ...values } : values;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success(editingCategory ? 'Đã cập nhật danh mục' : 'Đã tạo danh mục');
        setModalVisible(false);
        setEditingCategory(null);
        form.resetFields();
        fetchCategories();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      message.error('Không thể lưu danh mục');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourse = (course: MoodleCourse) => {
    setEditingCourse(course);
    setCourseModalVisible(true);
    setTimeout(() => {
      courseForm.resetFields();
      courseForm.setFieldsValue({
        fullname: course.fullname || '',
        shortname: course.shortname || '',
        idnumber: course.idnumber || '',
        categoryid: course.categoryid !== undefined && course.categoryid !== null ? course.categoryid : undefined
      });
    }, 0);
  };

  const handleDeleteCourse = async (course: MoodleCourse) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa khóa học này?',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await fetch(`/api/v1/moodle/courses/${course.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
            }
          });

          if (response.ok) {
            message.success('Đã xóa khóa học');
            fetchCourses();
          } else {
            throw new Error('Failed to delete course');
          }
        } catch (error) {
          console.error('Error deleting course:', error);
          message.error('Lỗi xóa khóa học');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleCourseModalOk = async () => {
    try {
      const values = await courseForm.validateFields();
      setLoading(true);

      const url = editingCourse ? `/api/v1/moodle/courses` : '/api/v1/moodle/courses';
      const method = editingCourse ? 'PUT' : 'POST';
      const payload = editingCourse ? { ...editingCourse, ...values } : values;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success(editingCourse ? 'Cập nhật khóa học thành công' : 'Tạo khóa học thành công');
        setCourseModalVisible(false);
        setEditingCourse(null);
        courseForm.resetFields();
        fetchCourses();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      if (!(error as any).errorFields) {
        message.error('Lỗi thao tác khóa học');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setModalVisible(true);
    setTimeout(() => {
      form.resetFields();
    }, 0);
  };

  const handleAddCourse = () => {
    setEditingCourse(null);
    setCourseModalVisible(true);
    setTimeout(() => {
      courseForm.resetFields();
      if (selectedCategoryId) {
        courseForm.setFieldsValue({ categoryid: selectedCategoryId });
      }
    }, 0);
  };

  const handleSelectCategory = (selectedKeys: React.Key[]) => {
    if (selectedKeys.length > 0) {
      const catId = parseInt(selectedKeys[0].toString());
      setSelectedCategoryId(catId);
      console.log('Selected category ID:', catId); // Debug log
    } else {
      setSelectedCategoryId(null);
    }
  };

  const renderTreeTitle = (nodeData: CategoryTreeNode) => {
    const courseCount = courses.filter(c => c.categoryid === nodeData.id).length;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span>{nodeData.name}</span>
        {courseCount > 0 && (
          <span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
            ({courseCount} khóa học)
          </span>
        )}
        <Tooltip title="Sửa">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            style={{ marginLeft: 8 }}
            onClick={(e) => {
              e.stopPropagation();
              const category = categories.find(c => c.id === nodeData.id);
              if (category) handleEditCategory(category);
            }}
          />
        </Tooltip>
        <Popconfirm
          title="Xóa danh mục?"
          onConfirm={(e) => {
            e?.stopPropagation();
            handleDeleteCategory(nodeData.id);
          }}
        >
          <Tooltip title="Xóa">
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              style={{ marginLeft: 4 }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popconfirm>
      </div>
    );
  };

  const filteredCourses = selectedCategoryId
    ? courses.filter(c => c.categoryid === selectedCategoryId)
    : [];

  const categoryColumns: ColumnsType<MoodleCategory> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên danh mục',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Mã số',
      dataIndex: 'idnumber',
      key: 'idnumber',
      width: 120,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Danh mục cha',
      dataIndex: 'parent',
      key: 'parent',
      width: 180,
      render: (parentId: number) => {
        const parent = categories.find(c => c.id === parentId);
        return parent ? parent.name : (parentId === 0 ? 'Gốc' : parentId.toString());
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditCategory(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Xóa danh mục?"
            onConfirm={() => handleDeleteCategory(record.id)}
          >
            <Tooltip title="Xóa">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const courseColumns: ColumnsType<MoodleCourse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên khóa học',
      dataIndex: 'fullname',
      key: 'fullname',
      width: 220,
      sorter: (a, b) => a.fullname.localeCompare(b.fullname),
    },
    {
      title: 'Tên ngắn',
      dataIndex: 'shortname',
      key: 'shortname',
      width: 120,
      sorter: (a, b) => a.shortname.localeCompare(b.shortname),
    },
    {
      title: 'Mã số',
      dataIndex: 'idnumber',
      key: 'idnumber',
      width: 120,
    },
    {
      title: 'Trạng thái',
      key: 'visible',
      width: 100,
      render: (_, record) => (
        <Tag color={record.visible ? 'green' : 'red'}>
          {record.visible ? 'Hiển thị' : 'Ẩn'}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 130,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem học viên">
            <Button
              type="text"
              icon={<UserOutlined />}
              onClick={() => showEnrolledUsersModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditCourse(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Xóa khóa học?"
            onConfirm={() => handleDeleteCourse(record)}
          >
            <Tooltip title="Xóa">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Columns for enrolled users table with built-in filters
  const enrolledUsersColumns: ColumnsType<EnrolledUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên đăng nhập',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      sorter: (a, b) => a.username.localeCompare(b.username),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc tên đăng nhập"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.username.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Họ tên',
      key: 'fullname',
      width: 200,
      sorter: (a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`),
      render: (_, record) => `${record.lastname} ${record.firstname}`,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc họ tên"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) =>
        `${record.lastname} ${record.firstname}`.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      sorter: (a, b) => a.email.localeCompare(b.email),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc email"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.email.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      sorter: (a, b) => (a.department || '').localeCompare(b.department || ''),
      render: (department) => department || 'Chưa xác định',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc phòng ban"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => (record.department || '').toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Vai trò',
      key: 'roles',
      width: 150,
      render: (_, record) => (
        <Space wrap>
          {record.roles && record.roles.map(role => (
            <Tag key={role.roleid} color="blue">{role.name}</Tag>
          ))}
        </Space>
      ),
      filters: [
        { text: 'Student', value: 'student' },
        { text: 'Teacher', value: 'teacher' },
        { text: 'Editing teacher', value: 'editingteacher' },
      ],
      onFilter: (value, record) => record.roles?.some(r => r.shortname === value) || false,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={24}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FolderOpenOutlined />
                <span>Quản lý danh mục Moodle</span>
              </div>
            }
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCategory}
                >
                  Thêm danh mục
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchCategories}
                />
              </Space>
            }
          >
            <Row gutter={32}>
              <Col span={10}>
                <div style={{
                  background: '#f7fafc',
                  borderRadius: 8,
                  padding: 20,
                  minHeight: 400,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
                    <FolderOpenOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    Sơ đồ danh mục
                  </div>
                  <Tree
                    showIcon
                    treeData={categoriesTree}
                    titleRender={renderTreeTitle}
                    defaultExpandAll
                    onSelect={handleSelectCategory}
                    style={{
                      background: 'transparent',
                      fontSize: 15,
                      zIndex: 1
                    }}
                  />
                </div>
              </Col>
              <Col span={14}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16
                }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCourse}
                    disabled={!selectedCategoryId}
                  >
                    Thêm khóa học
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchCourses}
                  />
                </div>
                <Table
                  columns={courseColumns}
                  dataSource={filteredCourses}
                  rowKey="id"
                  loading={loading}
                  bordered
                  pagination={{ pageSize: 10 }}
                  size="small"
                  style={{ borderRadius: 8 }}
                  locale={{
                    emptyText: selectedCategoryId
                      ? 'Không có khóa học nào trong danh mục này.'
                      : 'Chọn danh mục để xem khóa học.'
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Modal thêm/sửa danh mục */}
      <Modal
        title={editingCategory ? 'Cập nhật danh mục' : 'Thêm danh mục'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Tên danh mục"
            rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="idnumber"
            label="Mã số"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Mô tả"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="parent"
            label="Danh mục cha"
          >
            <Select
              showSearch
              placeholder="Chọn danh mục cha"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
            >
              <Select.Option value={0}>Gốc</Select.Option>
              {categories.map(category => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal thêm/sửa khóa học */}
      <Modal
        title={editingCourse ? 'Cập nhật khóa học' : 'Thêm khóa học'}
        open={courseModalVisible}
        onOk={handleCourseModalOk}
        onCancel={() => {
          setCourseModalVisible(false);
          setEditingCourse(null);
          courseForm.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form form={courseForm} layout="vertical">
          <Form.Item
            name="fullname"
            label="Tên khóa học"
            rules={[{ required: true, message: 'Vui lòng nhập tên khóa học' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="shortname"
            label="Tên ngắn"
            rules={[{ required: true, message: 'Vui lòng nhập tên ngắn' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="idnumber"
            label="Mã số"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="categoryid"
            label="Danh mục"
            rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
          >
            <Select
              showSearch
              placeholder="Chọn danh mục"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
            >
              {categories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal danh sách học viên */}
      <Modal
        title={`Danh sách học viên - ${selectedCourseForUsers?.fullname || ''}`}
        open={enrolledUsersModalVisible}
        onCancel={handleEnrolledUsersCancel}
        footer={[
          <Button key="close" onClick={handleEnrolledUsersCancel}>
            Đóng
          </Button>
        ]}
        width={1100}
      >
        {/* Info bar */}
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          Tổng cộng: <strong>{enrolledUsers.length}</strong> học viên. Sử dụng icon <SearchOutlined style={{ color: '#1890ff' }} /> trên tiêu đề cột để lọc dữ liệu.
        </div>

        {/* Table with Skeleton loading */}
        {usersLoading && enrolledUsers.length === 0 ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Table
            columns={enrolledUsersColumns}
            dataSource={enrolledUsers}
            rowKey="id"
            loading={{
              spinning: usersLoading && enrolledUsers.length > 0,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
              tip: 'Đang cập nhật...'
            }}
            pagination={{
              total: enrolledUsers.length,
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} học viên`,
            }}
            size="small"
            scroll={{ x: 950, y: 400 }}
            style={{
              transition: 'opacity 0.3s ease',
              opacity: usersLoading ? 0.7 : 1
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default MoodleCategoryManagementPage;
